import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, MapPin, CheckCircle2 } from "lucide-react";

interface DeliveryConfirmationPayload {
  photoUrl: string | null;
  notes: string | null;
  proofDistanceMeters: number | null;
  confirmedAt: string | null;
}

interface DeliveryProofCardProps {
  orderId: string;
  orderStatus: string | null | undefined;
}

export function DeliveryProofCard({ orderId, orderStatus }: DeliveryProofCardProps) {
  const enabled = Boolean(orderId) && String(orderStatus || "").toLowerCase() === "delivered";

  const { data } = useQuery<{ confirmation: DeliveryConfirmationPayload | null }>({
    queryKey: ["/api/orders", orderId, "delivery-confirmation"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/delivery-confirmation`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled,
  });

  if (!enabled) return null;
  const confirmation = data?.confirmation;
  if (!confirmation) return null;

  const distanceLabel =
    confirmation.proofDistanceMeters != null
      ? confirmation.proofDistanceMeters < 10
        ? "At the delivery address"
        : `${confirmation.proofDistanceMeters}m from the delivery address`
      : null;

  const confirmedAtLabel = confirmation.confirmedAt
    ? new Date(confirmation.confirmedAt).toLocaleString()
    : null;

  return (
    <Card className="mb-6 text-left" data-testid="delivery-proof-card">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          Delivery confirmed{confirmedAtLabel ? ` · ${confirmedAtLabel}` : ""}
        </div>

        {confirmation.photoUrl && (
          <div className="overflow-hidden rounded-lg border">
            <img
              src={confirmation.photoUrl}
              alt="Delivery proof"
              className="block w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 text-sm">
          {distanceLabel && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {distanceLabel}
            </div>
          )}
          {confirmation.notes && (
            <div className="flex items-start gap-2 text-muted-foreground">
              <Camera className="mt-0.5 h-4 w-4" />
              <span>{confirmation.notes}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
